import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import {isSessionExpiredError} from '../../../utils/authErrors';

import styles from './OperatorSection.module.css';
import dropdownArrow from '../../../static_files/dropdown-arrow.svg';
import checkGreen from '../../../static_files/check-circle-filled-green.svg';
import xRed from '../../../static_files/x-circle-red.svg';
import closeButton from '../../../static_files/closeButtonHover.svg';

const RegisterRobotModal = ({
    onClose,
    onRefreshData // (Optional) Refresh the robot data in the parent component on success to include the newly registered robot
}) => {
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [isManufacturerOpen, setIsManufacturerOpen] = useState(false);
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [registrationOptions, setRegistrationOptions] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serialValid, setSerialValid] = useState(null);        // true | false | null
    const [serialValidating, setSerialValidating] = useState(false);
    const debounceRef = useRef(null);

    const { userId, gatewayToken, showNotification, handleUserSessionExpired } = useContext(SwapMainAppContext);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/operator/get-robot-registration-options?user_id=${userId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${gatewayToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                const result = await response.json();
                if (result.success) {
                    setRegistrationOptions(result.data);
                } else {
                    showNotification('error', 'Failed to load registration options. Please try again later.');
                }
            } catch (err) {
                showNotification('error', 'Failed to load registration options. Please try again later.');
            }
        };
        fetchOptions();
    }, []);

    // Debounced serial validation — fires 1500ms after user stops typing
    useEffect(() => {
        setSerialValid(null);
        setSerialValidating(false);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!serialNumber || !manufacturer || !model) return;

        setSerialValidating(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const response = await fetch(`${PRISMAX_BACKEND_URL}/api/operator/register-robot/validate-serial-number`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${gatewayToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ user_id: userId, manufacturer, model, serial_number: serialNumber }),
                });
                const result = await response.json();
                setSerialValid(result.success ? result.valid : null);
            } catch {
                setSerialValid(null);
            } finally {
                setSerialValidating(false);
            }
        }, 1500);

        return () => clearTimeout(debounceRef.current);
    }, [serialNumber, manufacturer, model]);

    const handleManufacturerSelect = (value) => {
        setManufacturer(value);
        setModel('');
        setIsManufacturerOpen(false);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/operator/register-robot`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${gatewayToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId,
                    robot_data: { manufacturer, model, serial_number: serialNumber },
                }),
            });
            const result = await response.json();
            if (result.success) {
                if (onRefreshData) await onRefreshData(); // Optionally refresh the robot data in the parent component, so it includes the newly registered robot
                showNotification('success', 'Robot registered successfully.');
                onClose();
            } else {
                if (isSessionExpiredError(result.msg)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', result.msg || 'Failed to register robot. Please try again.');
            }
        } catch (err) {
            showNotification('error', 'Failed to register robot. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isRegisterButtonDisabled = isSubmitting || serialValidating || serialValid === false || !manufacturer || !model || !serialNumber;

    const manufacturerList = Object.keys(registrationOptions);
    const modelList = manufacturer ? (registrationOptions[manufacturer] || []) : [];

    return ReactDOM.createPortal(
        <div className={styles.registerModalOverlay} onMouseDown={onClose}>
            <div className={styles.registerModalContainer} onMouseDown={(e) => e.stopPropagation()}>

                <h2 className={styles.registerModalTitle}>Register New Robot</h2>

                <button className={styles.registerModalCloseButton} onClick={onClose}>
                    <img className={styles.registerModalCloseIcon} src={closeButton}  alt="close" />
                </button>

                <div className={styles.registerModalFieldsArea}>

                    {/* Manufacturer */}
                    <div className={styles.registerModalFieldRow}>
                        <label className={styles.registerModalLabel}>Manufacturer:</label>
                        <div className={styles.registerModalDropdownWrapper}>
                            <button
                                className={styles.registerModalDropdownTrigger}
                                onClick={() => { setIsManufacturerOpen((p) => !p); setIsModelOpen(false); }}
                            >
                                <span className={styles.registerModalDropdownValue}>{manufacturer || ''}</span>
                                <img
                                    src={dropdownArrow}
                                    alt="expand"
                                    className={`${styles.registerModalDropdownArrow} ${isManufacturerOpen ? styles.registerModalDropdownArrowOpen : ''}`}
                                />
                            </button>
                            {isManufacturerOpen && (
                                <ul className={styles.registerModalDropdownList}>
                                    {manufacturerList.map((value) => (
                                        <li
                                            key={value}
                                            className={`${styles.registerModalDropdownItem} ${manufacturer === value ? styles.registerModalDropdownItemSelected : ''}`}
                                            onMouseDown={() => handleManufacturerSelect(value)}
                                        >
                                            {value}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Model */}
                    <div className={styles.registerModalFieldRow}>
                        <label className={styles.registerModalLabel}>Model:</label>
                        <div className={styles.registerModalDropdownWrapper}>
                            <button
                                className={styles.registerModalDropdownTrigger}
                                onClick={() => { setIsModelOpen((p) => !p); setIsManufacturerOpen(false); }}
                                disabled={!manufacturer}
                            >
                                <span className={styles.registerModalDropdownValue}>{model || ''}</span>
                                <img
                                    src={dropdownArrow}
                                    alt="expand"
                                    className={`${styles.registerModalDropdownArrow} ${isModelOpen ? styles.registerModalDropdownArrowOpen : ''}`}
                                />
                            </button>
                            {isModelOpen && (
                                <ul className={styles.registerModalDropdownList}>
                                    {modelList.map((value) => (
                                        <li
                                            key={value}
                                            className={`${styles.registerModalDropdownItem} ${model === value ? styles.registerModalDropdownItemSelected : ''}`}
                                            onMouseDown={() => { setModel(value); setIsModelOpen(false); }}
                                        >
                                            {value}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Serial Number */}
                    <div className={styles.registerModalFieldRow}>
                        <label className={styles.registerModalLabel}>Serial Number:</label>
                        <div className={styles.registerModalSerialWrapper}>
                            <input
                                className={styles.registerModalSerialInput}
                                type="text"
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                            />
                            {serialValidating && (
                                <span className={styles.registerModalSerialSpinner} />
                            )}
                            {!serialValidating && serialValid === true && (
                                <img src={checkGreen} alt="valid" className={styles.registerModalSerialIcon} />
                            )}
                            {!serialValidating && serialValid === false && (
                                <img src={xRed} alt="invalid" className={styles.registerModalSerialIcon} />
                            )}
                        </div>
                    </div>

                </div>

                <div className={styles.registerModalButtonRow}>
                    <button className={styles.registerModalCancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={styles.registerModalRegisterButton}
                        onClick={handleSubmit}
                        disabled={isRegisterButtonDisabled}
                    >
                        {isSubmitting ? 'Registering...' : 'Register Robot'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default RegisterRobotModal;
